import React, { useEffect, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

interface MultiSelectDropdownProps {
    label: string;
    options: string[];
    selected: string[];
    onChange: (values: string[]) => void;
    allLabel?: string;
    className?: string;
}

/** Dropdown genérico multi-select — variante simplificada (sem agrupamento) de
 * ResponsibleFilter, para filtros novos que não precisam de hierarquia (cidade, origem,
 * status do funil, canal). */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label, options, selected, onChange, allLabel = 'Todos', className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pending, setPending] = useState<string[]>([...selected]);

    useEffect(() => { if (isOpen) setPending([...selected]); }, [isOpen]);

    const hasChanges = (() => {
        if (pending.length !== selected.length) return true;
        const a = [...pending].sort(), b = [...selected].sort();
        return a.some((v, i) => v !== b[i]);
    })();

    const toggle = (value: string) => {
        setPending(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const apply = () => { onChange([...pending]); setIsOpen(false); };
    const cancel = () => { setPending([...selected]); setIsOpen(false); };

    return (
        <div className={`relative w-full md:w-auto ${className}`}>
            <button
                onClick={() => setIsOpen(o => !o)}
                className={`flex items-center gap-2 px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group ${isOpen ? 'ring-2 ring-primary/10 border-primary' : ''}`}
            >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[140px]">
                    {selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label}: ${selected.length}`}
                </span>
                <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-[60] bg-black/30 md:bg-transparent" onClick={cancel}></div>
                    <div className="fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full md:left-0 mt-0 md:mt-2 w-full md:w-64 bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-[70] flex flex-col max-h-[80vh] md:max-h-[50vh] pb-4 md:pb-0">
                        <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto my-3 md:hidden shrink-0"></div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 mb-2 shrink-0">{label}</p>

                        <div className="flex-1 overflow-y-auto px-2 pb-1 custom-scrollbar">
                            <label className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${pending.length === 0 ? 'bg-gray-50 dark:bg-gray-700' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={pending.length === 0}
                                    onChange={() => setPending([])}
                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{allLabel}</span>
                            </label>
                            <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>
                            {options.map(opt => (
                                <label key={opt} className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={pending.includes(opt)}
                                        onChange={() => toggle(opt)}
                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700 shrink-0"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{opt}</span>
                                </label>
                            ))}
                            {options.length === 0 && <p className="text-xs text-gray-400 px-3 py-2">Nenhuma opção disponível.</p>}
                        </div>

                        <div className="shrink-0 px-3 pt-2 pb-3 border-t border-gray-100 dark:border-gray-700 mt-1">
                            <div className="flex items-center gap-2">
                                <button onClick={cancel} className="flex-1 px-3 py-2 md:py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
                                    Cancelar
                                </button>
                                <button
                                    onClick={apply}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 md:py-1.5 text-sm font-bold rounded-lg transition-all ${hasChanges ? 'bg-primary text-white hover:bg-primary/90 shadow-sm' : 'bg-primary/60 text-white/80 cursor-default'}`}
                                >
                                    <Check size={14} /> Aplicar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
